import { CustomItemComponentProps, Virtualizer, VirtualizerHandle } from 'virtua';

import { debounce, throttle } from 'es-toolkit';
import { ComponentChild, createContext } from 'preact';
import { forwardRef } from 'preact/compat';
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'preact/hooks';
import waitForTimers from 'wait-for-timers';
import { Episode } from '../models/podcasts';
import { useBumpEpisodePriorityWhenInView } from '../utils/bump-episode-priority-when-in-view';
import { exposeOnWindow } from '../utils/helpers';
import { topNavHeight } from '../utils/ui-measurements';
import { EpisodeItem, SwipeAction } from './episode-item';

type StickyState = {
  activeIndex: number;
  stickyIndexes: Set<number>;
};
const StickyHeadingContext = createContext<StickyState>({
  activeIndex: -1,
  stickyIndexes: new Set(),
});

// Adapted from https://github.com/inokawa/virtua/blob/6170c60618c99fbbfd9aec9d453aac94717393f5/stories/react/advanced/Sticky%20Group.stories.tsx#L30
const StickyItem = forwardRef<HTMLDivElement, CustomItemComponentProps>(
  ({ children, style, index }, ref) => {
    const { stickyIndexes, activeIndex } = useContext(StickyHeadingContext);
    return (
      <div
        ref={ref}
        style={{
          ...style,
          ...(stickyIndexes.has(index) && {
            zIndex: 1,
            background: 'white',
          }),
          ...(activeIndex === index && {
            position: 'sticky',
            top: 'var(--header-height)', // Already includes safe area
          }),
        }}
      >
        {children}
      </div>
    );
  }
);

export function VirtualizedEpisodesList({
  episodes,
  swipeAction,
  stickyHeadingIndices,
}: {
  episodes: (Episode | ComponentChild)[];
  swipeAction?: SwipeAction | ((ep: Episode) => SwipeAction | undefined);
  stickyHeadingIndices?: number[];
}) {
  const { scrollRef, offsetDivRef, scrollContainerOffset } =
    useScrollContainerAndOffset();

  const bumpEpisodePriorityWhenInView = useBumpEpisodePriorityWhenInView();
  const [activeIndex, setActiveIndex] = useState(-1);

  const stickyState = useMemo(() => {
    return {
      activeIndex,
      stickyIndexes: new Set(stickyHeadingIndices || []),
    };
  }, [stickyHeadingIndices, activeIndex]);

  const onScrollThrottled = useMemo(() => {
    return throttle(() => {
      if (!ref.current) return;
      const topItemIndex = ref.current.findStartIndex();
      let activeStickyIndex = stickyHeadingIndices?.findLast(
        (index) => topItemIndex >= index
      );
      if (activeStickyIndex === undefined) return setActiveIndex(-1);

      const nextStickyIndex = stickyHeadingIndices?.findLast(
        (index) => topItemIndex + 1 >= index
      );
      if (nextStickyIndex !== undefined && nextStickyIndex > activeStickyIndex) {
        const nextOffsetInList = ref.current.getItemOffset(nextStickyIndex);
        const distanceFromTopNext =
          nextOffsetInList + scrollContainerOffset - ref.current.scrollOffset;
        // Keep in sync with `--header-height` and test w/ safe areas!
        if (distanceFromTopNext < topNavHeight) {
          activeStickyIndex = nextStickyIndex;
        }
      }
      setActiveIndex(activeStickyIndex);
    }, 16);
  }, [setActiveIndex, stickyHeadingIndices, scrollContainerOffset]);

  const ref = useRef<VirtualizerHandle>(null);

  // Prevents error when no episodes are passed. Not sure exactly why but it seems Virtualizer
  // requires a child to be rendered
  if (!episodes.length) return null;

  return (
    <>
      <div ref={offsetDivRef}></div>
      <StickyHeadingContext.Provider value={stickyState}>
        <Virtualizer
          data={episodes}
          startMargin={scrollContainerOffset}
          scrollRef={scrollRef}
          ref={(virtualizerHandle) => {
            ref.current = virtualizerHandle;
            exposeOnWindow({ virtualizerHandle }); // For scroll restoration
          }}
          keepMounted={activeIndex === -1 ? undefined : [activeIndex]}
          item={StickyItem}
          onScroll={onScrollThrottled}
        >
          {(episode) =>
            !(episode instanceof Episode) ? (
              episode // Some other component (e.g. a heading)
            ) : (
              <EpisodeItem
                episode={episode}
                showThumbnail={false}
                swipeAction={
                  typeof swipeAction === 'function' ? swipeAction(episode) : swipeAction
                }
                key={episode.id}
                createContentRef={bumpEpisodePriorityWhenInView}
              ></EpisodeItem>
            )
          }
        </Virtualizer>
      </StickyHeadingContext.Provider>
    </>
  );
}

function useScrollContainerAndOffset() {
  // Virtua needs to know the scroll container so it can actually virtualize (otherwise it renders all children)
  const scrollRef = useRef<HTMLDivElement>(null);
  scrollRef.current = window.pageScrollContainer;

  // Virtua needs to know the offset between its top and the top of the scroll container
  const offsetDivRef = useRef<HTMLDivElement>(null);
  const [scrollContainerOffset, setScrollContainerOffset] = useState(0);
  const setOffsetTop = useCallback(
    debounce(() => {
      if (offsetDivRef.current)
        setScrollContainerOffset(offsetDivRef.current.offsetTop);
    }, 10),
    []
  );
  useLayoutEffect(() => setOffsetTop(), []);
  useEffect(() => {
    addEventListener('resize', setOffsetTop);
    return () => removeEventListener('resize', setOffsetTop);
  });

  // Prevent smooth scrolling on scroll container when restoring scroll position
  useLayoutEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.style.scrollBehavior = 'auto';
      return waitForTimers([100], () => {
        if (scrollRef.current) scrollRef.current.style.scrollBehavior = '';
      });
    }
  }, []);

  return { scrollRef, offsetDivRef, scrollContainerOffset };
}
